import type {
  TransformedCheckoutDetail,
  TransformedVehicleOption,
  TransformedFeature,
  PricingSummaryItem,
} from '../inventory/transformers.checkout';
import { NL_NON_TAXABLE_FEE_CODES, NL_STATUTORY_FEE_CODES } from '../inventory/dto.inventory';
import type { QuoteFileMetadata } from './dto.quotes';

/*
|--------------------------------------------------------------------------
| QUOTE VEHICLE DETAILS TYPES (from API response)
|--------------------------------------------------------------------------
*/

type QuoteVehicleOption = {
  label: string;
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

type QuoteFeature = {
  name: string;
  code: string;
  type: string;
  value: number;
  description?: string;
};

type QuotePricing = {
  basePrice: number;
  addOns: number;
  subtotal: number;
  configurationSubtotal?: number;
  fees: PricingSummaryItem[];
  credits: { label: string; value: number }[];
  deposit: number;
  total: number;
  taxRate?: number;
  taxAmount?: number;
};

type QuoteVehicleDetails = {
  vin: string;
  shortcode: string;
  vehicleImage: string;
  model: string;
  modelYear: string;
  trim: string;
  type: string;
  availability: string;
  currencyCode: string;
  vehicleOptions: QuoteVehicleOption[];
  features: QuoteFeature[];
  pricing: QuotePricing;
};

export type QuoteDetailsResponse = {
  quoteIdentifier: string;
  sfdcQuoteIdentifier: string | null;
  prospectIdentifier: string;
  businessPartnerUserIdentifier: string;
  businessPartnerOrganizationIdentifier: string;
  prospectType: string;
  prospectDetails: Record<string, unknown>;
  companyName: string;
  companyTaxIdentification: string;
  duns?: {
    dunsNumber?: string;
    primaryName?: string;
    companyName?: string;
    isOutOfBusiness?: boolean;
  };
  invoiceAddressLine1: string;
  invoiceAddressLine2: string;
  invoiceCity: string;
  invoiceCountry: string;
  invoicePostalCode: string;
  deliveryTimeline: string;
  isDeliveryThisQuarter: boolean;
  model: string;
  trim: string;
  vin: string;
  vinBatchNumber?: string;
  vehicleCondition: string;
  isBuildToOrder: boolean;
  configurationShortCode: string;
  isWinterWheelsRequired: boolean;
  winterWheelsQuantity: number;
  financingType: string;
  preferredPaymentMethod: string;
  paymentMethod?: string; // Legacy field, use preferredPaymentMethod
  quotationDate: string;
  sharedDate: string;
  expirationDate: string;
  acceptedDate: string;
  quoteCurrency: string;
  totalAmount: number | null;
  isTermsAndConditionsAccepted: boolean;
  vehicleDetails: QuoteVehicleDetails;
  vehiclePricingDetails: {
    items: { label: string; amount: number }[];
  };
  taxesAndFeesDetails: Record<string, unknown>;
  discountsAndIncentivesDetails: {
    label?: string;
    amount?: number;
    cashDiscount?: string;
    percentageDiscount?: string | number;
    agentDiscountAmount?: string | null;
    agentDiscountPercentage?: string | number | null;
    lucidDiscountAmount?: string | null;
    lucidDiscountPercentage?: string | number | null;
    requestedIncentives?: {
      charging_voucher?: boolean;
      trade_in_support?: boolean;
    };
    agencyDiscountPercentageCap?: string;
    agencyCashDiscountCap?: string;
    agencyExtraCashDiscount?: string;
  };
  financingDetails: {
    financeCompanyName?: string;
    financeCompanyAddress?: string;
    financeCity?: string;
    financeCountry?: string;
    financePostalCode?: string;
    financeVat?: string;
    duns?: {
      dunsNumber?: string;
      primaryName?: string;
      companyName?: string;
      isOutOfBusiness?: boolean;
    };
  } & Record<string, unknown>;
  quotationDetails: Record<string, unknown>;
  quoteFilesMetadata?: QuoteFileMetadata[];
  vehicleHoldStartDatetime: string;
  vehicleHoldExpiryDatetime: string;
  docusignEmailAddress: string;
  docusignStatus: string;
  lastSyncedToSalesforce: string | null;
  timezone: string;
  isActive: boolean;
  isDeleted: boolean;
  createdDatetime: string;
  updatedDatetime: string;
  createdBy: string;
  updatedBy: string | null;
  currentStatus: string;
  statusHistory: unknown;
};

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

/** Mapping of API prospect type values to form dropdown values */
const PROSPECT_TYPE_MAP: Record<string, string> = {
  private: 'Private',
  business: 'Business',
};

/**
 * Maps API prospect type values to form dropdown values
 * @param value - Raw prospect type from API (e.g., 'private', 'BUSINESS')
 * @returns Normalized prospect type for form (e.g., 'Private', 'Business')
 */
const mapProspectType = (value: string | undefined): string => {
  if (!value) return '';
  return PROSPECT_TYPE_MAP[value.toLowerCase()] ?? value.toLowerCase();
};

/**
 * Maps API payment method values to form dropdown values
 * @param value - Raw payment method from API
 * @returns Normalized payment method for form
 */
const mapPaymentMethod = (value: string | undefined): string => {
  if (!value) return '';
  const paymentMethodMap: Record<string, string> = {
    cash: 'Cash',
    lfs: 'LFS Loan',
    lfs_loan: 'LFS Loan',
    lfs_lease: 'LFS Lease',
    '3rd_party_finance': '3rd Party Financing',
    third_party_finance: '3rd Party Financing',
  };

  return paymentMethodMap[value.toLowerCase()] || value;
};

/**
 * Transforms quote vehicle options to the standard format
 * Excludes 'model' label as it's not a user-selectable option
 */
const transformVehicleOptions = (
  vehicleOptions: QuoteVehicleOption[] | undefined
): TransformedVehicleOption[] => {
  if (!vehicleOptions) return [];

  return vehicleOptions
    .filter((option) => option.label !== 'model')
    .map((option) => ({
      label: option.label || '',
      name: option.name || '',
      code: option.code || '',
      type: option.type || '',
      value: option.value ?? 0,
      description: option.description,
    }));
};

/**
 * Transforms quote features to the standard format
 */
const transformFeatures = (features: QuoteFeature[] | undefined): TransformedFeature[] => {
  if (!features) return [];

  return features.map((feature) => ({
    name: feature.name || '',
    code: feature.code || '',
    type: feature.type || '',
    value: feature.value ?? 0,
    description: feature.description,
  }));
};

/**
 * Extracts the base price (variant value) from vehicle options
 */
const extractBasePrice = (vehicleOptions: QuoteVehicleOption[] | undefined): number => {
  const variantItem = vehicleOptions?.find((item) => item.label === 'variant');
  return variantItem?.value ?? 0;
};

const toOptionalString = (value: string | number | null | undefined): string | undefined =>
  value === undefined || value === null ? undefined : String(value);

const roundCurrency = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const resolveTaxAmount = (
  persistedTaxAmount: number | undefined,
  subtotal: number,
  configurationSubtotal: number,
  fees: PricingSummaryItem[]
): number | undefined => {
  const isNlPricing = fees.some((fee) => fee.code && NL_STATUTORY_FEE_CODES.has(fee.code));
  if (!isNlPricing || subtotal <= 0) return persistedTaxAmount;

  const taxableFees = fees.reduce(
    (sum, fee) => (fee.code && NL_NON_TAXABLE_FEE_CODES.has(fee.code) ? sum : sum + fee.value),
    0
  );
  const originalVat = roundCurrency(subtotal - configurationSubtotal - taxableFees);
  return originalVat >= 0 ? originalVat : persistedTaxAmount;
};

/*
|--------------------------------------------------------------------------
| TRANSFORMER FUNCTION
|--------------------------------------------------------------------------
*/

/**
 * Transforms the quote details API response to a TransformedCheckoutDetail format
 * This allows the quote data to be used with the same UI components as checkout data
 * @param response - The raw quote details API response
 * @returns Transformed checkout detail object
 * @example
 * ```typescript
 * const transformed = transformQuoteToCheckoutDetail(quoteResponse);
 * console.log(transformed.model); // 'air'
 * console.log(transformed.vehicleOptions[0].name); // 'Touring'
 * ```
 */
export const transformQuoteToCheckoutDetail = (
  response: QuoteDetailsResponse
): TransformedCheckoutDetail => {
  const { vehicleDetails } = response;
  const pricing = vehicleDetails?.pricing;
  const { discountsAndIncentivesDetails, financingDetails, quotationDetails } = response;

  // Transform vehicle options and features first (needed for configurationSubtotal calculation)
  const vehicleOptions = transformVehicleOptions(vehicleDetails?.vehicleOptions);
  const features = transformFeatures(vehicleDetails?.features);

  // Extract pricing values
  const basePrice = extractBasePrice(vehicleDetails?.vehicleOptions);
  const addOns = pricing?.addOns ?? 0;
  const subtotal = pricing?.subtotal ?? 0;
  const deposit = pricing?.deposit ?? 0;
  const total = pricing?.total ?? 0;
  const taxRate = pricing?.taxRate ?? 0;

  // Calculate configurationSubtotal: basePrice + vehicleOptions values + features values
  // Exclude 'variant' from optionsTotal since basePrice already includes it
  const optionsTotal = vehicleOptions
    .filter((option) => option.label !== 'variant')
    .reduce((sum, option) => sum + (option.value ?? 0), 0);
  const featuresTotal = features.reduce((sum, feature) => sum + (feature.value ?? 0), 0);
  const configurationSubtotal =
    pricing?.configurationSubtotal ?? basePrice + optionsTotal + featuresTotal;

  // Extract fees and credits
  const fees: PricingSummaryItem[] =
    pricing?.fees?.map((fee) => ({
      label: fee.label,
      value: fee.value,
      ...(fee.code ? { code: fee.code } : {}),
      ...(fee.taxAmount !== undefined ? { taxAmount: fee.taxAmount } : {}),
      ...(fee.valueAfterTax !== undefined ? { valueAfterTax: fee.valueAfterTax } : {}),
    })) ?? [];

  const taxAmount = resolveTaxAmount(pricing?.taxAmount, subtotal, configurationSubtotal, fees);

  const credits: PricingSummaryItem[] =
    pricing?.credits
      ?.filter((credit) => credit.label !== 'RESERVATION_PAYMENT')
      ?.map((credit) => ({
        label: credit.label,
        value: credit.value,
      })) ?? [];

  return {
    // Vehicle identification
    vin: response.vin || vehicleDetails?.vin || '',
    vinBatchNumber: response.vinBatchNumber || '',
    expirationDate: response.expirationDate || '',
    sharedDate: response.sharedDate || '',
    shortcode: vehicleDetails?.shortcode || '',
    vehicleImage: vehicleDetails?.vehicleImage || '',

    // Vehicle details
    model: vehicleDetails?.model || '',
    modelYear: vehicleDetails?.modelYear || '',
    trim: vehicleDetails?.trim || '',
    type: vehicleDetails?.type || '',
    availability: vehicleDetails?.availability || '',

    // Currency
    currencyCode: vehicleDetails?.currencyCode || 'USD',

    // Vehicle options and features (already transformed above)
    vehicleOptions,
    features,

    // Pricing breakdown
    pricing: {
      basePrice,
      addOns,
      subtotal,
      configurationSubtotal,
      fees,
      credits,
      deposit,
      total,
      taxRate,
      taxAmount,
    },
    summary: {
      prospectType: mapProspectType(response.prospectType),
      preferredPaymentMethod: mapPaymentMethod(
        response.preferredPaymentMethod || response['paymentMethod']
      ),
      deliveryTimeline: response.deliveryTimeline,
      ...financingDetails,
      ...quotationDetails,
      companyName: response.companyName,
      companyTaxIdentification: response.companyTaxIdentification,
      invoiceAddressLine1: response.invoiceAddressLine1,
      invoiceCity: response.invoiceCity,
      invoiceCountry: response.invoiceCountry,
      invoicePostalCode: response.invoicePostalCode,
      cashDiscount: discountsAndIncentivesDetails?.cashDiscount,
      percentageDiscount: toOptionalString(discountsAndIncentivesDetails?.percentageDiscount),
      agentDiscountAmount: discountsAndIncentivesDetails?.agentDiscountAmount ?? undefined,
      agentDiscountPercentage: toOptionalString(
        discountsAndIncentivesDetails?.agentDiscountPercentage
      ),
      lucidDiscountAmount: discountsAndIncentivesDetails?.lucidDiscountAmount ?? undefined,
      lucidDiscountPercentage: toOptionalString(
        discountsAndIncentivesDetails?.lucidDiscountPercentage
      ),
      requestedIncentives: discountsAndIncentivesDetails?.requestedIncentives,
      // D&B business entity data (for edit mode)
      duns: response.duns,
      // Finance provider details
      financeCompanyName: financingDetails?.financeCompanyName,
      financeCompanyAddress: financingDetails?.financeCompanyAddress,
      financeCity: financingDetails?.financeCity,
      financeCountry: financingDetails?.financeCountry,
      financePostalCode: financingDetails?.financePostalCode,
      financeVat: financingDetails?.financeVat,
      // D&B finance entity data (for edit mode)
      financeDuns: financingDetails?.duns,
      // Quotation details
      agencyDiscountPercentageCap: discountsAndIncentivesDetails?.agencyDiscountPercentageCap,
      agencyCashDiscountCap: discountsAndIncentivesDetails?.agencyCashDiscountCap,
      agencyExtraCashDiscount: discountsAndIncentivesDetails?.agencyExtraCashDiscount,
    },
    quoteFilesMetadata: response.quoteFilesMetadata,
    docusignStatus: response.docusignStatus || '',
    currentStatus: response.currentStatus || '',
  };
};
